import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type {
  AreasFilters,
  AreaCreateInput,
  AreaUpdateInput,
} from '@/modules/users/types/area'
import * as areasApi from '@/modules/users/api/areasApi'

export const areasKeys = {
  all: ['areas'] as const,
  lists: () => [...areasKeys.all, 'list'] as const,
  list: (filters?: AreasFilters) => [...areasKeys.lists(), { filters }] as const,
  allList: () => [...areasKeys.all, 'all'] as const,
  details: () => [...areasKeys.all, 'detail'] as const,
  detail: (id: number) => [...areasKeys.details(), id] as const,
}

export function useAreas(filters?: AreasFilters) {
  return useQuery({
    queryKey: areasKeys.list(filters),
    queryFn: () => areasApi.getAreas(filters),
  })
}

export function useAllAreas() {
  return useQuery({
    queryKey: areasKeys.allList(),
    queryFn: () => areasApi.getAllAreas(),
  })
}

export function useArea(areaId: number) {
  return useQuery({
    queryKey: areasKeys.detail(areaId),
    queryFn: () => areasApi.getArea(areaId),
    enabled: !!areaId,
  })
}

export function useCreateArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AreaCreateInput) => areasApi.createArea(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      toast.success('Área creada exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear área')
    },
  })
}

export function useUpdateArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ areaId, data }: { areaId: number; data: AreaUpdateInput }) =>
      areasApi.updateArea(areaId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      queryClient.invalidateQueries({ queryKey: areasKeys.detail(variables.areaId) })
      toast.success('Área actualizada exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar área')
    },
  })
}

export function useDeleteArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (areaId: number) => areasApi.deleteArea(areaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      toast.success('Área eliminada exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar área')
    },
  })
}

export function useToggleAreaActive() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (areaId: number) => areasApi.toggleAreaActive(areaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      toast.success('Estado del área actualizado')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al cambiar estado del área')
    },
  })
}

export function useAssignUserToArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ areaId, userId }: { areaId: number; userId: number }) =>
      areasApi.assignUserToArea(areaId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      toast.success('Usuario asignado al área')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al asignar usuario')
    },
  })
}

export function useRemoveUserFromArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ areaId, userId }: { areaId: number; userId: number }) =>
      areasApi.removeUserFromArea(areaId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      toast.success('Usuario removido del área')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al remover usuario')
    },
  })
}
